import { useState } from "react";
import { useLocation, useNavigate } from "react-router";

import type {
	CustomerQuoteBoardItem,
	QuoteBoardItem,
} from "~/shared/schemas/quote-board-schema";
import { sendQuoteBoardEditQuoteStepEvent } from "~/shared/utils/google-analytics-helper.ts";

import {
	addReturnTo,
	buildQuoteBoardReturnUrl,
} from "../utils/quote-board-return-url.ts";
import { resolveQuoteBoardStatus } from "../utils/quote-board-status.ts";

type UseQuoteStatusActionArgs = {
	dataItem: QuoteBoardItem | CustomerQuoteBoardItem;
	isInternal?: boolean;
	isDisabled?: boolean;
	onOpenCarrierSelection?: (quoteId: number) => void;
};

/**
 * Layout-agnostic status resolution + click behavior shared by the grid's
 * `QuoteStatusCell` and the mobile `QuoteBoardCard` status button, so both
 * stay in lockstep with navigate/open-modal behavior.
 */
export const useQuoteStatusAction = ({
	dataItem,
	isInternal = false,
	isDisabled,
	onOpenCarrierSelection,
}: UseQuoteStatusActionArgs) => {
	const navigate = useNavigate();
	const location = useLocation();
	const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);

	const { className, label, action } = resolveQuoteBoardStatus(dataItem, {
		isInternal,
	});

	const isError = action.kind === "open-error-modal";
	// Opening the carrier modal needs a host to render it; without one the row is inert.
	const isActionable =
		action.kind !== "none" &&
		(action.kind !== "open-carrier-modal" || onOpenCarrierSelection != null);

	const handleClick = () => {
		switch (action.kind) {
			case "open-error-modal": {
				setIsErrorModalOpen(true);
				return;
			}
			case "open-carrier-modal": {
				sendQuoteBoardEditQuoteStepEvent(String(dataItem.quoteId), label);
				onOpenCarrierSelection?.(dataItem.quoteId);
				return;
			}
			case "navigate": {
				sendQuoteBoardEditQuoteStepEvent(String(dataItem.quoteId), label);
				const destination = action.withReturnTo
					? addReturnTo(
							action.to,
							buildQuoteBoardReturnUrl(location.pathname, location.search),
						)
					: action.to;
				if (dataItem.bulkTenderStatus === "details_needed") {
					navigate(destination, { state: { detailsNeededRepair: true } });
				} else {
					navigate(destination);
				}
				return;
			}
			case "none": {
				return;
			}
		}
	};

	return {
		className,
		label,
		isError,
		disabled: !isActionable || isDisabled,
		title: dataItem.bulkTenderReason ?? undefined,
		handleClick,
		isErrorModalOpen,
		closeErrorModal: () => setIsErrorModalOpen(false),
		errorModalTitle: dataItem.bulkTenderStatus
			? "Bulk Tender Error"
			: "Bulk Upload Error",
		errorMessage:
			dataItem.bulkTenderReason ?? dataItem.bulkUploadException ?? "",
		showCopyQuoteInErrorModal: Boolean(dataItem.bulkTenderStatus),
		quoteId: dataItem.quoteId,
	};
};
